import React, { useEffect, useState } from 'react';
import { Line } from 'react-chartjs-2';
import { Chart, CategoryScale, LinearScale, TimeScale, PointElement, LineElement, Title, Tooltip, Legend } from 'chart.js';
import zoomPlugin from 'chartjs-plugin-zoom';
import 'chartjs-adapter-moment';
import axiosInstance from './axiosConfig';
import {
  Grid, Card, CardContent, Typography, TextField, Button, Box, CircularProgress, Stack, IconButton, Select, MenuItem, FormControl, InputLabel
} from '@mui/material';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import RefreshIcon from '@mui/icons-material/Refresh';
import moment from 'moment-timezone';
import { CircularProgressbar, buildStyles } from 'react-circular-progressbar';
import 'react-circular-progressbar/dist/styles.css';
import { useTemperatureUnit } from './TemperatureUnitContext';
import { useLanguage } from '../contexts/LanguageContext';

Chart.register(CategoryScale, LinearScale, TimeScale, PointElement, LineElement, Title, Tooltip, Legend, zoomPlugin);

const SensorChart = ({ roomId, dataType }) => {
  const { temperatureUnit, convertTemperature } = useTemperatureUnit();
  const { translate } = useLanguage();
  const [data, setData] = useState([]);
  const [from, setFrom] = useState(moment().subtract(1, 'days').toISOString());
  const [to, setTo] = useState(moment().toISOString());
  const [lastValues, setLastValues] = useState({ temperature: 0, humidity: 0, co2: 0 });
  const [loading, setLoading] = useState(false);
  const [viewMode, setViewMode] = useState('average'); // 'average' o 'sensor'
  const [sensors, setSensors] = useState([]); // Lista de sensores en el room
  const [selectedSensorId, setSelectedSensorId] = useState(null); // Sensor seleccionado

  // Recuperar projectId del localStorage
  const projectId = localStorage.getItem('projectId');

  // Obtener la lista de sensores en el room
  const fetchSensors = async () => {
    try {
      const response = await axiosInstance.get('/api/registered-sensors', {
        params: { projectId, roomId },
      });
      setSensors(response.data);
      if (response.data.length > 0) {
        setSelectedSensorId(response.data[0].id); // Selecciona el primer sensor por defecto
      }
    } catch (error) {
      console.error('Error fetching sensors', error);
    }
  };

  // Obtener los datos según el modo seleccionado (average o sensor)
  const fetchData = async () => {
    setLoading(true);
    try {
      let endpoint;
      let params = { from, to, projectId };

      if (viewMode === 'average') {
        endpoint = `/api/rooms/${roomId}/latest`;
      } else {
        endpoint = `/api/data/${selectedSensorId}`;
        params.roomId = roomId; // Incluir roomId para datos de un sensor específico
      }

      const response = await axiosInstance.get(endpoint, { params });
      const fetchedData = response.data;
      setData(Array.isArray(fetchedData) ? fetchedData : [fetchedData]);

      // Actualizar lastValues
      if (Array.isArray(fetchedData)) {
        const lastEntry = fetchedData[fetchedData.length - 1];
        setLastValues({
          temperature: lastEntry.temperature,
          humidity: lastEntry.humidity,
          co2: lastEntry.co2,
          soil_moisture: lastEntry.soil_moisture,
        });
      } else {
        setLastValues(fetchedData);
      }
    } catch (error) {
      console.error('Error fetching data', error);
    }
    setLoading(false);
  };

  useEffect(() => {
    if (projectId && roomId) {
      fetchSensors();
    }
  }, [projectId, roomId]);

  useEffect(() => {
    if (viewMode === 'sensor' && !selectedSensorId) return; // No hacer fetch si no hay sensor seleccionado
    fetchData();
  }, [from, to, viewMode, selectedSensorId]);

  const getColor = (type, value) => {
    const thresholds = {
      temperature: { min: 18, max: 28 },
      humidity: { min: 40, max: 70 },
      co2: { min: 300, max: 1500 },
      soil_moisture: { min: 50, max: 70 },
    }[type];
    return value < thresholds.min ? '#1E90FF' : value <= thresholds.max ? '#4CAF50' : '#FF0000';
  };

  const chartData = {
    labels: data.map(entry => moment(entry.timestamp).format('YYYY-MM-DD HH:mm:ss')),
    datasets: [
      {
        label: `${dataType === 'temperature' ? 'Temperature' : dataType === 'humidity' ? 'Humidity' : dataType === 'co2' ? 'CO2' : 'Soil Moisture'}`,
        data: data.map(entry => entry[dataType]),
        borderColor: 'rgba(255, 99, 132, 1)',
        fill: false,
      },
    ],
  };

  const chartOptions = {
    plugins: {
      legend: { labels: { color: '#666' } },
      zoom: {
        pan: { enabled: true, mode: 'x' },
        zoom: {
          wheel: { enabled: true },
          pinch: { enabled: true },
          mode: 'x',
        },
      },
    },
    scales: {
      x: { ticks: { color: '#666' }, type: 'time', time: { unit: 'hour' } },
      y: { ticks: { color: '#666' } },
    },
  };

  const handleTimeRange = (hours) => {
    setFrom(moment().subtract(hours, 'hours').toISOString());
    setTo(moment().toISOString());
  };

  return (
    <Box sx={{ padding: 2 }}>
      <Grid container spacing={2}>
        {/* Selector de Modo (Average o Sensor) */}
        <Grid item xs={12}>
          <FormControl fullWidth>
            <InputLabel>{translate('sensorChart.visualizationMode')}</InputLabel>
            <Select
              value={viewMode}
              onChange={(e) => setViewMode(e.target.value)}
              label={translate('sensorChart.visualizationMode')}
            >
              <MenuItem value="average">{translate('sensorChart.averageMode')}</MenuItem>
              <MenuItem value="sensor">{translate('sensorChart.sensorMode')}</MenuItem>
            </Select>
          </FormControl>
        </Grid>

        {/* Selector de Sensor (solo en modo "sensor") */}
        {viewMode === 'sensor' && (
          <Grid item xs={12}>
            <FormControl fullWidth>
              <InputLabel>Seleccionar Sensor</InputLabel>
              <Select
                value={selectedSensorId || ''}
                onChange={(e) => setSelectedSensorId(e.target.value)}
                label="Seleccionar Sensor"
              >
                {sensors.map((sensor) => (
                  <MenuItem key={sensor.id} value={sensor.id}>
                    {sensor.name} (ID: {sensor.identifier})
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
        )}

        {/* Indicadores de valores recientes con círculos */}
        {['temperature', 'humidity', 'co2', 'soil_moisture'].map((type) => (
          lastValues[type] !== undefined && (
            <Grid item xs={12} sm={3} key={type}>
              <Card elevation={2}>
                <CardContent>
                  <Stack direction="row" alignItems="center" spacing={2}>
                    <AccessTimeIcon />
                    <Typography variant="h6" component="div">
                      {type.charAt(0).toUpperCase() + type.slice(1)}
                    </Typography>
                  </Stack>
                  <Box sx={{ width: 100, height: 100, mx: 'auto', mt: 2 }}>
                    <CircularProgressbar
                      value={lastValues[type]}
                      maxValue={
                        type === 'co2' ? 2000
                        : type === 'humidity' ? 100
                        : type === 'soil_moisture' ? 100
                        : 40
                      }
                      text={`${
                        type === 'temperature'
                          ? convertTemperature(lastValues[type])
                          : lastValues[type]
                      }${type === 'co2' ? ' ppm' : type === 'temperature' ? (temperatureUnit === 'Fahrenheit' ? '°F' : '°C') : '%'}`}
                      styles={buildStyles({
                        textColor: '#FFFFFF',
                        pathColor: getColor(type, lastValues[type]),
                        trailColor: '#333',
                      })}
                    />
                  </Box>
                </CardContent>
              </Card>
            </Grid>
          )
        ))}

        {/* Gráfica de datos */}
        <Grid item xs={12}>
          <Card elevation={2}>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                {translate('sensorChart.dataChart')}
              </Typography>

              <Box sx={{ display: 'flex', justifyContent: 'center', mb: 2, gap: 1 }}>
                {[1, 4, 24, 72, 168].map(hours => (
                  <Button key={hours} variant="outlined" onClick={() => handleTimeRange(hours)}>
                    {hours === 1 ? translate('sensorChart.lastHour') : translate('sensorChart.lastHours', { hours })}
                  </Button>
                ))}
                <IconButton onClick={() => Chart.getChart('sensor-chart').resetZoom()} color="primary">
                  <RefreshIcon />
                </IconButton>
              </Box>

              <Box sx={{ display: 'flex', gap: 2, justifyContent: 'center', mb: 3 }}>
                <TextField
                  label="Desde"
                  type="date"
                  value={moment(from).format('YYYY-MM-DD')}
                  onChange={(e) => setFrom(moment(e.target.value).toISOString())}
                  InputLabelProps={{ shrink: true }}
                />
                <TextField
                  label="Hasta"
                  type="date"
                  value={moment(to).format('YYYY-MM-DD')}
                  onChange={(e) => setTo(moment(e.target.value).toISOString())}
                  InputLabelProps={{ shrink: true }}
                />
                <Button onClick={fetchData} variant="contained">
                  Buscar
                </Button>
              </Box>

              {loading ? (
                <Box display="flex" justifyContent="center" mt={2}>
                  <CircularProgress />
                </Box>
              ) : (
                <Line id="sensor-chart" data={chartData} options={chartOptions} />
              )}
            </CardContent>
          </Card>
        </Grid>
      </Grid>
    </Box>
  );
};

export default SensorChart;